import { getLanguage, type SupportedLanguage } from './language';

type TimeParts = {
  h24: number;
  h12: number;
  m: number;
};

function timeParts(epochMs: number): TimeParts {
  const date = new Date(epochMs);
  const h24 = date.getHours();
  return {
    h24,
    h12: h24 % 12 || 12,
    m: date.getMinutes(),
  };
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

/** Sky X ML (v1) chat timestamp examples — 3:30 AM / 3:30 PM reference row. */
const CHAT_TIME_FORMATTERS: Record<
  SupportedLanguage,
  (parts: TimeParts) => string
> = {
  en: ({ h12, h24, m }) => `${h12}.${pad2(m)} ${h24 < 12 ? 'AM' : 'PM'}`,
  es: ({ h24, m }) => `${h24}:${pad2(m)} h`,
  de: ({ h24, m }) => `${h24}:${pad2(m)} Uhr`,
  fr: ({ h24, m }) => `${h24} h ${pad2(m)}`,
  da: ({ h24, m }) => `${pad2(h24)}.${pad2(m)}`,
  fi: ({ h12, h24, m }) => `${h12}.${pad2(m)} ${h24 < 12 ? 'AM' : 'PM'}`,
  el: ({ h12, h24, m }) => `${h12}.${pad2(m)} ${h24 < 12 ? 'π.μ.' : 'μ.μ.'}`,
  id: ({ h24, m }) => `${pad2(h24)}.${pad2(m)}`,
  it: ({ h24, m }) => `${pad2(h24)}.${pad2(m)}`,
  ja: ({ h12, h24, m }) => `${h24 < 12 ? '午前' : '午後'}${h12}時${m}分`,
  ko: ({ h12, h24, m }) => `${h12}.${pad2(m)} ${h24 < 12 ? 'AM' : 'PM'}`,
  lv: ({ h12, h24, m }) => `${h12}.${pad2(m)} ${h24 < 12 ? 'AM' : 'PM'}`,
  lt: ({ h24, m }) => `${h24}.${pad2(m)} val.`,
  ms: ({ h12, h24, m }) => `${h12}.${pad2(m)} ${h24 < 12 ? 'PAGI' : 'PETANG'}`,
  nb: ({ h24, m }) => `${pad2(h24)}.${pad2(m)}`,
  pl: ({ h24, m }) => `${h24}:${pad2(m)}`,
  pt: ({ h12, h24, m }) => `${h12}:${pad2(m)} ${h24 < 12 ? 'AM' : 'PM'}`,
  ro: ({ h12, h24, m }) => `${h12}:${pad2(m)} ${h24 < 12 ? 'AM' : 'PM'}`,
  ru: ({ h24, m }) => `${h24}:${pad2(m)}`,
  sr: ({ h12, h24, m }) =>
    `${h12},${pad2(m)} ${h24 < 12 ? 'pre podne' : 'posle podne'}`,
  sv: ({ h24, m }) => `${pad2(h24)}.${pad2(m)}`,
  th: ({ h24, m }) => `${pad2(h24)}.${pad2(m)} น.`,
  tr: ({ h24, m }) => `${pad2(h24)}.${pad2(m)}`,
  vi: ({ h12, h24, m }) => `${h12}:${pad2(m)} ${h24 < 12 ? 'sáng' : 'chiều'}`,
  zh: ({ h12, h24, m }) => `${h24 < 12 ? '上午' : '下午'} ${h12}:${pad2(m)}`,
  'zh-TW': ({ h12, h24, m }) =>
    `${h24 < 12 ? '上午' : '下午'} ${h12}:${pad2(m)}`,
  bg: ({ h12, h24, m }) =>
    `${h12}:${pad2(m)} ч. ${h24 < 12 ? 'сутринта' : 'следобед'}`,
};

/** Format chat message time per Sky X locale rules (ML v1 sheet). */
export function formatChatMessageTime(
  epochMs: number | null | undefined,
  language?: SupportedLanguage
): string {
  if (epochMs == null || !Number.isFinite(epochMs) || epochMs <= 0) {
    return '';
  }

  const lang = language ?? getLanguage();
  const formatter = CHAT_TIME_FORMATTERS[lang] ?? CHAT_TIME_FORMATTERS.en;
  return formatter(timeParts(epochMs));
}
